import { subDays } from 'date-fns'
import { useState } from 'react'
import { useAccount } from '@/api/hooks/use-account'
import type { AnalyticsParams } from '@/api/hooks/use-analytics'
import { useAnalytics } from '@/api/hooks/use-analytics'
import type { DateRange } from '@/components/ui/date-range-picker'
import { DateRangePicker } from '@/components/ui/date-range-picker'
import { MetricCard } from '@/components/ui/metric-card'
import { AnalyticsByAdvertiser } from '@/pages/analytics-by-advertiser'
import { AnalyticsByCampaign } from '@/pages/analytics-by-campaign'
import { EmailTypeSplit } from '@/pages/analytics-email-type-split'
import { GranularitySelector } from '@/pages/analytics-granularity'
import { ISPBreakdownTable } from '@/pages/analytics-isp-table'
import { RateChart } from '@/pages/analytics-rate-chart'
import { VolumeChart } from '@/pages/analytics-volume-chart'

type Granularity = 'hour' | 'day' | 'week'
type AnalyticsTab = 'overview' | 'campaigns' | 'advertisers'

const TABS: { id: AnalyticsTab; label: string }[] = [
  { id: 'overview', label: 'Overview' },
  { id: 'campaigns', label: 'Campaigns' },
  { id: 'advertisers', label: 'Advertisers' },
]

const DANGER_THRESHOLD_BOUNCE = 3
const DANGER_THRESHOLD_COMPLAINT = 0.05

export function AnalyticsPage(): React.JSX.Element {
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview')
  const [dateRange, setDateRange] = useState<DateRange>({
    from: subDays(new Date(), 7),
    to: new Date(),
  })
  const [granularity, setGranularity] = useState<Granularity>('day')

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h1 className="text-2xl font-semibold text-text-primary">Analytics</h1>
        {activeTab === 'overview' && (
          <div className="flex flex-wrap items-center gap-3">
            <DateRangePicker value={dateRange} onChange={setDateRange} />
            <GranularitySelector value={granularity} onChange={setGranularity} />
          </div>
        )}
      </div>

      <div className="flex gap-1 border-b border-surface-3">
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            onClick={() => setActiveTab(tab.id)}
            className={
              activeTab === tab.id
                ? 'border-b-2 border-brand-500 px-4 py-2 text-sm font-medium text-brand-500'
                : 'px-4 py-2 text-sm font-medium text-text-tertiary hover:text-text-primary'
            }
          >
            {tab.label}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <AnalyticsOverview dateRange={dateRange} granularity={granularity} />
      )}
      {activeTab === 'campaigns' && <AnalyticsByCampaign />}
      {activeTab === 'advertisers' && <AnalyticsByAdvertiser />}
    </div>
  )
}

function AnalyticsOverview({
  dateRange,
  granularity,
}: {
  dateRange: DateRange
  granularity: Granularity
}): React.JSX.Element {
  const params: AnalyticsParams = {
    from: dateRange.from,
    to: dateRange.to,
    granularity,
  }
  const { data, isLoading } = useAnalytics(params)
  const accountQuery = useAccount()

  if (isLoading) {
    return <div className="py-12 text-center text-text-tertiary">Loading analytics...</div>
  }

  const totals = data?.totals

  // Convert timeseries rate fields from ratios (0-1) to percentages (0-100)
  const timeseries = (data?.timeseries ?? []).map((p) => ({
    ...p,
    delivery_rate: p.delivery_rate * 100,
    bounce_rate: p.bounce_rate * 100,
    complaint_rate: p.complaint_rate * 100,
    open_rate: p.open_rate * 100,
    click_rate: p.click_rate * 100,
  }))

  // Convert ISP rates from ratios to percentages
  const ispBreakdown = (data?.isp_breakdown ?? []).map((row) => ({
    ...row,
    delivery_rate: row.delivery_rate * 100,
    open_rate: row.open_rate * 100,
  }))

  // Analytics returns rates as ratios (0-1), convert to percentages
  const deliveryPct = totals ? totals.delivery_rate * 100 : 0
  const bouncePct = totals ? totals.bounce_rate * 100 : 0
  const complaintPct = totals ? totals.complaint_rate * 100 : 0
  const openPct = totals ? totals.open_rate * 100 : 0
  const clickPct = totals ? totals.click_rate * 100 : 0

  // Email type split from account data
  const account = accountQuery.data?.account
  const totalSent = account?.emails_sent_this_period ?? 0
  const marketingSent = account?.marketing_emails_sent_this_period ?? 0
  const transactionalSent = totalSent - marketingSent

  return (
    <div className="space-y-6">
      {totals && (
        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-5">
          <MetricCard
            label="Delivery Rate"
            value={fmtPct(deliveryPct)}
            variant={deliveryPct >= 98 ? 'success' : deliveryPct >= 95 ? 'warning' : 'danger'}
          />
          <MetricCard
            label="Bounce Rate"
            value={fmtPct(bouncePct)}
            variant={bouncePct <= DANGER_THRESHOLD_BOUNCE ? 'success' : 'danger'}
          />
          <MetricCard
            label="Complaint Rate"
            value={fmtPct(complaintPct)}
            variant={complaintPct <= DANGER_THRESHOLD_COMPLAINT ? 'success' : 'danger'}
          />
          <MetricCard label="Open Rate" value={fmtPct(openPct)} />
          <MetricCard label="Click Rate" value={fmtPct(clickPct)} />
        </div>
      )}

      <VolumeChart data={timeseries} />

      <div className="grid gap-6 lg:grid-cols-2">
        <RateChart
          data={timeseries}
          title="Delivery Rate"
          dataKey="delivery_rate"
          color="var(--color-success)"
          dangerLine={95}
        />
        <RateChart
          data={timeseries}
          title="Open & Click Rates"
          dataKey="open_rate"
          secondaryKey="click_rate"
          color="var(--color-brand-500)"
          secondaryColor="var(--color-info)"
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-2">
        <RateChart
          data={timeseries}
          title="Bounce & Complaint Trend"
          dataKey="bounce_rate"
          secondaryKey="complaint_rate"
          color="var(--color-danger)"
          secondaryColor="var(--color-warning)"
          dangerLine={DANGER_THRESHOLD_BOUNCE}
        />
        <EmailTypeSplit transactional={transactionalSent} marketing={marketingSent} />
      </div>

      <ISPBreakdownTable data={ispBreakdown} />
    </div>
  )
}

function fmtPct(value: number): string {
  return `${value.toFixed(2)}%`
}

